<template></template>

<script>
  import { v1 as uuidv1 } from 'uuid'
  import Video from 'twilio-video'
  import axios from 'axios'
  import { defaultMediaOpts } from '../utils/twilioHelpers'
  import { promiseTimeout } from '@/../../shared/src/utils.js'
  import { lambdaAuthHeaders } from '@/../../shared/src/utils/encryption.js'
  import { twilioTokenUrlBase } from '@/../../shared/src/utils/constants.js'
  /**
  * Component that holds common twilio functionality
   * Include this in any component that wants to establish a twilio connection
  */

  export default {
    name: 'TwilioMixin',
    data () {
      return {
        localTracks: [],
        logLevel: 'warn',
        statsInterval: null,
        knownStatsIssues: [
          'RTCPeerConnection#getStats() not supported',
          'Cannot read property \'localAudioTrackStats\' of undefined',
          'getStats() is not supported on this version of Safari due to this bug: https://bugs.webkit.org/show_bug.cgi?id=192601',
          'track must be associated with a unique sender or receiver, but is associated with 0 senders and 0 receivers.'
        ],
        roomCompleted: false
      }
    },

    props: {
      'roomName': {
        'type': String,
        'default': 'learcubeTwilioRoom'
      },
      'roomType': {
        'type': String,
        'default': 'peer-to-peer'
      },
      'roomRecording': {
        'type': Boolean,
        'default': false
      },
      'gatherStatsTimer': {
        'type': Number,
        'default': 1000
      }
    },

    computed: {

      userId () {
        return (this.config && this.config.user) ? this.config.user.id : '0'
      },

      twilioEventId () {
        return this.roomName.slice(0, 120)
      },

      tokenUrl () {
        const userId = (this.config && this.config.user) ? this.config.user.id : '0'
        return `${twilioTokenUrlBase}/${this.twilioEventId}/${userId}/?room-type=${this.roomType}&recording=${this.roomRecording}`
      },

      completeUrl () {
        const userId = (this.config && this.config.user) ? this.config.user.id : '0'
        return `${this.config.lcApiBase}/twilio-room/complete/${this.twilioEventId}/${userId}/`
      }
    },

    methods: {

      getNamedLocalTracks () {
        const opts = Object.assign({}, this.mediaOpts || defaultMediaOpts())
        const videoOpts = Object.assign({}, opts.video, {'name': `cam-${this.userId}`})
        const audioOpts = Object.assign({}, opts.audio, {'name': `mic-${this.userId}`})

        const trackPromises = [
          Video.createLocalAudioTrack(audioOpts),
          new Video.LocalDataTrack({'name': `data-${this.userId}`})
        ]

        if (opts.video) {
          trackPromises.push(Video.createLocalVideoTrack(videoOpts))
        }

        return Promise.all(trackPromises)
      },

      getLocalTracks () {
        // Get the local tracks from the users device
        const opts = Object.assign(this.mediaOpts || defaultMediaOpts(), {})
        return promiseTimeout(Video.createLocalTracks(opts), 12000)
      },

      getNativeLocalTracks () {
        const opts = Object.assign(this.mediaOpts || defaultMediaOpts(), {})
        return navigator.mediaDevices.getUserMedia(opts)
      },

      getTwilioTracksFromMediastreamTracks (tracks) {
        return tracks.map((track) => {
          if (track.kind === 'video') {
            return new Video.LocalVideoTrack(track)
          }
          if (track.kind === 'audio') {
            return new Video.LocalAudioTrack(track)
          }
        })
      },

      getDataTrack () {
        return new Promise((resolve, reject) => {
          return resolve([new Video.LocalDataTrack()])
        })
      },

      requestRoom (tokenUrl) {
        // Make Ajax request to Learncube view that returns a token from twilio
        const url = tokenUrl || this.tokenUrl
        return axios.get(url, {'headers': lambdaAuthHeaders(uuidv1()), 'timeout': 10000})
      },

      completeRoom () {
        this.roomCompleted = true
        return axios.get(this.completeUrl)
      },

      connectToRoom (roomToken, roomName, opts = {}) {
        // Create a connection options object with the room name
        const connectOptions = Object.assign({
          name: roomName,
          region: 'gll',
          tracks: this.localTracks,
          logLevel: this.logLevel,
          dominantSpeaker: true,
          networkQuality: {
            local: 3, // LocalParticipant's Network Quality verbosity [1 - 3]
            remote: 3 // RemoteParticipants' Network Quality verbosity [0 - 3]
          },
          enableDscp: true,
          bandwidthProfile: {
            video: {
              mode: 'grid'
              // renderDimensions: {
              //   high: {width: 960, height: 540},
              //   standard: {width: 640, height: 480},
              //   low: {width: 176, height: 144}
              // }
            },
            preferredVideoCodecs: ['VP8', 'H264'],
            offerExtmapAllowMixed: false,
            chromeSpecificConstraints: {
              mandatory: {
                // Enable CPU overuse detection
                googCpuOveruseDetection: true,
                // Not sure about this, but Google Hangouts uses it
                googCpuOveruseEncodeUsage: true,
                // The CPU % threshold for overuse
                googCpuOveruseThreshold: 85
              }
            }
          }
        }, opts)

        // Connect the the room using the connection options
        return Video.connect(roomToken, connectOptions)
      },

      initAndConnect () {
        console.log('Overwrite this function with bespoke initialisation and connection code')
        console.log('You can use the examples below as a guide')

        // return this.getLocalTracks()
        //   .then((tracks) => {
        //     this.localTracks = tracks
        //     return this.getDataTrack()
        //   })
        //   .then((dataTracks) => {
        //     this.localTracks = this.localTracks.concat(dataTracks)
        //     return this.requestRoom()
        //   })
        //   .then((twilioResp) => {
        //     return this.connectToRoom(twilioResp.data.token, this.roomName)
        //   })
        //   .then((room) => {
        //     this.bindRoomEvents(room)
        //     return this.room
        //   })
        //   .then((room) => {
        //     return this.gatherStats(room)
        //   })
        //   .catch((err) => {
        //     console.log('Some error in the twilio workflow', err)
        //   })
      },

      bindRoomEvents (room) {
        console.log('Overwrite this method to add handlers to twilio room events')
      },

      disconnect (r) {
        const room = r || this.room
        if (room) {
          this.stopStats()
          return room.disconnect()
        }
      },

      reconnect (room) {
        this.disconnect(room)
        this.initAndConnect()
      },

      gatherStats (room) {
        this.stopStats()
        this.statsInterval = setInterval(() => {
            return room.getStats()
              .then((statsReports) => {
                return this.actionStats(statsReports)
              })
            .catch((error) => {
              if (this.knownStatsIssues.includes(error.message)) {
                this.stopStats()
              }
              return this.logError({'type': 'errors/twilioGatherStatsError', 'payload': {'error': {'message': error.message}}})
            })
        }, this.gatherStatsTimer)
      },

      stopStats () {
        if (this.statsInterval) {
          clearInterval(this.statsInterval)
        }
      },

      actionStats (report) {
        console.log('Overwrite this method to anayse and log statistics from the twilio video call')
      }

    },

    beforeDestroy () {
      this.stopStats()
    }
  }
</script>


<docs></docs>
